import React from 'react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

export type HeroQuickMenuIcon = React.ComponentType<{ className?: string }>;

export interface HeroQuickMenuItem {
  key: string;
  icon: HeroQuickMenuIcon;
  iconClassName?: string;
  hoverClassName?: string;
  active?: boolean;
  activeClassName?: string;
  label: string;
  value: React.ReactNode;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface HeroQuickMenuCardProps {
  className?: string;
  borderClassName?: string;
  heroClassName?: string;
  heroContent: React.ReactNode;
  items: HeroQuickMenuItem[];
  columns?: 2 | 3 | 4 | 5 | 6 | 7;
  stripClassName?: string;
}

export const HeroQuickMenuCard: React.FC<HeroQuickMenuCardProps> = ({
  className,
  borderClassName,
  heroClassName,
  heroContent,
  items,
  columns = 6,
  stripClassName,
}) => {
  return (
    <div className={cn('rounded-xl border overflow-hidden shadow-sm', borderClassName, className)}>
      <div className={cn('px-5 pt-5 pb-4', heroClassName)}>{heroContent}</div>

      <div data-columns={columns} className={cn('bg-white flex flex-nowrap divide-x border-t overflow-x-auto', stripClassName)}>
        {items.map((item) => {
          const Icon = item.icon;
          const body = (
            <div
              className={cn(
                'flex flex-col items-center justify-center py-3 px-2 gap-1 transition-colors',
                item.disabled ? 'opacity-60 cursor-default' : 'cursor-pointer',
                item.active && (item.activeClassName ?? 'bg-primary/5 border-b-2 border-primary'),
                item.hoverClassName ?? 'hover:bg-muted/40'
              )}
            >
              <Icon className={cn('h-5 w-5', item.iconClassName)} />
              <span className={cn('text-xs font-semibold leading-tight', item.active ? 'text-primary' : 'text-foreground')}>
                {item.label}
              </span>
              <span className={cn('text-[11px] leading-none', item.active ? 'text-primary/70' : 'text-muted-foreground')}>
                {item.value}
              </span>
            </div>
          );

          if (item.href && !item.disabled) {
            return (
              <Link key={item.key} to={item.href} className="min-w-[8rem] flex-1 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-inset">
                {body}
              </Link>
            );
          }

          if (item.onClick && !item.disabled) {
            return (
              <button
                key={item.key}
                type="button"
                className="min-w-[8rem] flex-1 border-0 bg-transparent p-0 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-inset"
                onClick={item.onClick}
              >
                {body}
              </button>
            );
          }

          return <div key={item.key} className="min-w-[8rem] flex-1">{body}</div>;
        })}
      </div>
    </div>
  );
};
